import React, { useMemo } from "react";
import { getPositionDepartment } from "@/Common/helpers/Positions";
import { IMappedPosition } from "@/Common/types/positions/api";
import { Box, Grid } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ReviewerSelector from "./ReviewerSelector";
import Title from "../../../common/Title";
import { IUser } from "@/Common/interfaces/user";
import { IWashoutReviewersObject } from "@/Settings/types/Common/model";
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";
import { errorLogger } from "@/Common/helpers/logger";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

import { IDepartmentHierarchy } from "@/Settings/types/SystemSettings/washoutReviewers";
import { getDepartmentHierarchy } from "@/Settings/api/SystemSettings/queries";
import CommonDivider from "../../../common/CommonDivider";

type TWashoutReviewersProps = {
	locationId: string;
	mappedPositions?: IMappedPosition[];
	washoutReviewers?: IWashoutReviewersObject[];
	disabled: boolean;
};

type TDepartment = { label: string; positions: { name: string }[] };

type TReviewerInput = {
	position: string;
	reviewerId?: string;
	lastModifiedBy?: string;
	isDirectSupervisor: boolean;
};

const WashoutReviewers = React.memo((props: TWashoutReviewersProps) => {
	const { locationId, mappedPositions = [], disabled, washoutReviewers } = props;
	const user = useSelector((state: RootState) => state.user.user);

	const departments = useMemo(
		() =>
			mappedPositions.reduce<TDepartment[]>((acc, value) => {
				const department = getPositionDepartment(value.systemPosition);
				const foundDepartment = acc.find((item: TDepartment) => item.label === department);
				if (foundDepartment) {
					foundDepartment.positions.push({ name: value.systemPosition });
				} else {
					acc.push({ label: department, positions: [{ name: value.systemPosition }] });
				}
				return acc;
			}, []),
		[mappedPositions, locationId]
	).sort((a, b) => (a.label > b.label ? 1 : -1));

	const { data: departmentsHierarchy, isLoading: isLoadingDepartmentsHierarchy } = useQuery({
		queryKey: ["getDepartmentHierarchy", locationId],
		queryFn: async () => {
			const result = await getDepartmentHierarchy(
				locationId,
				departments.map((item) => item.label)
			);
			return result.map((item: { _id: string; department: string; path: string }) => ({ ...item, path: JSON.parse(item.path) }));
		},
		enabled: !!locationId && !!departments.length,
		staleTime: Infinity,
	});

	const { mutate, isPending } = useMutation({
		mutationFn: (input: TReviewerInput) => updateSystemSettings(locationId, { washoutReviewers: input }, "Washout Reviewers"),
		onError: (error) => errorLogger(error),
	});

	const queryClient = useQueryClient();
	const { getUsersSystemSettings: users = [] } = queryClient.getQueryData(["systemSettingsAdditionalInfo", locationId]) as {
		getUsersSystemSettings: IUser[];
	};

	const filteredUsers = useMemo(
		() => users?.filter((item) => item.locationId === locationId && item.currentStatus?.status === "ACTIVE"),
		[users]
	);

	const handleUpdate = (params: { selected: IUser | null; isDirectSupervisor: boolean; position: string }) => {
		const { selected, isDirectSupervisor, position } = params;

		const input = {
			lastModifiedBy: user?._id ?? "",
			reviewerId: selected?._id ?? "",
			position,
			isDirectSupervisor,
		};

		mutate(input);
	};

	if (!filteredUsers?.length || isLoadingDepartmentsHierarchy) {
		return null;
	}

	return (
		<Grid>
			{departments.map((department, index) => {
				const keyIndex = `${department.label}-${index}`;
				const isNotLastOne = index !== departments.length - 1;
				return (
					<Grid key={keyIndex}>
						<Title title={department.label} className="!mt-[16px]" />
						<Box className="flex flex-wrap justify-start w-[99%]">
							{department.positions.map((position, positionIndex) => {
								const keyPositionIndex = `${position.name}-${positionIndex}`;
								const washoutReviewer = washoutReviewers?.find((item) => item.position === position.name);
								const departmentHierarchy = (departmentsHierarchy as unknown as IDepartmentHierarchy[])?.find(
									(path) => path.department === department.label
								);

								return (
									<ReviewerSelector
										key={keyPositionIndex}
										title={position.name}
										users={filteredUsers}
										disabled={disabled || isPending}
										uniqueKey={`${department.label}-${keyPositionIndex}`}
										washoutReviewer={washoutReviewer}
										onUpdate={handleUpdate}
										departmentHierarchy={departmentHierarchy}
									/>
								);
							})}
						</Box>
						{isNotLastOne && <CommonDivider className="!ml-0" />}
					</Grid>
				);
			})}
		</Grid>
	);
});

export default WashoutReviewers;
